import { Box, CircularProgress, Typography, Alert, Snackbar } from "@mui/material";
import { useParams, useLocation } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import DocumentSection from "./DocumentSection";
import { fetchDocumentsByCompany, getFileContentUrl, deleteDocument } from "../../../../services/documentsService";

/** Section title to API type: Investment Recommendations -> ir, Board Decks -> boarddeck */
function sectionTitleToType(title) {
  if (title === "Investment Recommendations") return "ir";
  if (title === "Board Decks") return "boarddeck";
  if (title === "Portfolio Company Updates") return "ir";  // ← ADD
  return null;
}

export default function Documents() {
  const { companyId } = useParams();
  const { state } = useLocation();
  const partnerCode = state?.partner_doc ? String(state.partner_doc) : "";
  const dealId = state?.deal_id ? String(state.deal_id) : "";
  const companyName = companyId ? decodeURIComponent(companyId) : "";

  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteSuccessSnackbarOpen, setDeleteSuccessSnackbarOpen] = useState(false);
  const [deleteErrorSnackbarOpen, setDeleteErrorSnackbarOpen] = useState(false);
  const [deleteErrorMessage, setDeleteErrorMessage] = useState("");

  const loadDocuments = useCallback(async () => {
    if (!partnerCode || !dealId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await fetchDocumentsByCompany(partnerCode, dealId);
      setSections(data.sections ?? []);
    } catch (e) {
      setError(e?.message || "Failed to load documents");
      setSections([]);
    } finally {
      setLoading(false);
    }
  }, [partnerCode, dealId]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const handleDelete = async (doc, type) => {
    try {
      await deleteDocument(doc.path);
      // Remove the document locally from the appropriate section
      setSections(prev => prev.map(sec => {
        if (sectionTitleToType(sec.title) === type) {
          return { ...sec, documents: (sec.documents || []).filter(d => d.path !== doc.path) };
        }
        return sec;
      }));
      setDeleteSuccessSnackbarOpen(true);
    } catch (error) {
      setDeleteErrorMessage(error?.message || "Failed to delete document");
      setDeleteErrorSnackbarOpen(true);
    }
  };

  const handleAdd = (newDoc, type) => {
    // Add the document locally to the appropriate section
    setSections(prev => prev.map(sec => {
      if (sectionTitleToType(sec.title) === type) {
        return { ...sec, documents: [...(sec.documents || []), newDoc] };
      }
      return sec;
    }));
  };

  const handleProgress = () => { };
  const handleCancel = () => { };

  if (!partnerCode || !dealId) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3, marginLeft: "30px", marginTop: "20px" }}>
        <Typography color="text.secondary">Select a company to view documents.</Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3, marginLeft: "30px", marginTop: "20px" }}>
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
        <Typography color="text.secondary">You can try again or check back later.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3, marginLeft: "30px", marginTop: "20px" }}>
      {sections.map((section) => (
        <DocumentSection
          key={section.title}
          section={section}
          documents={section.documents ?? []}
          companyName={companyName}
          onAdd={handleAdd}
          onDelete={handleDelete}
          onProgress={handleProgress}
          onCancel={handleCancel}
          getFileContentUrl={getFileContentUrl}
          partnerCode={partnerCode}
          dealId={dealId}
        />
      ))}

      {/* ── Delete success toast (green) ── */}
      <Snackbar
        open={deleteSuccessSnackbarOpen}
        autoHideDuration={6000}
        onClose={() => setDeleteSuccessSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setDeleteSuccessSnackbarOpen(false)}
          severity="success"
          variant="filled"
          sx={{ width: "100%" }}
        >
          Document deleted successfully
        </Alert>
      </Snackbar>

      {/* ── Delete error toast (red) ── */}
      <Snackbar
        open={deleteErrorSnackbarOpen}
        autoHideDuration={6000}
        onClose={() => setDeleteErrorSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setDeleteErrorSnackbarOpen(false)}
          severity="error"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {deleteErrorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}